import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Card,
  Alert,
  CircularProgress,
} from '@mui/material';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useBeforeUnload, unstable_usePrompt as usePrompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ALL_SUBSCRIPTIONS } from '../../constants';
import { useAuth, useCompany, useSubscription, useSubscriptionsAdmin } from '../../stores';
import { timestampToDate } from '../../utils/date';
import SubscriptionEditDrawer from '../../components/SubscriptionEditDrawer';
import { SubscriptionEdit } from '../../model/subscription';

const NO_RESELLER = 'NO_RESELLER';

const Subscriptions = observer(() => {
  const auth = useAuth();
  const companyStore = useCompany();
  const subscriptionStore = useSubscription();
  const subscriptionsAdminStore = useSubscriptionsAdmin();

  const { companyKey, data: company } = companyStore;

  const { resellers } = subscriptionsAdminStore;
  const [choosenReseller, setChoosenReseller] = useState<string>(NO_RESELLER);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionEdit | null>(null);
  const [loading, setLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      subscriptionStore.resetMessage();
    };
  }, []);

  useEffect(() => {
    if (company?.resellerId) {
      setChoosenReseller(company?.resellerId || NO_RESELLER);
    }
  }, [companyStore.data]);

  useBeforeUnload((event) => {
    if (unsavedChanges) {
      event.preventDefault();
    }
  });

  usePrompt({
    when: unsavedChanges,
    message: t('subscriptionScreen.unsavedChangesAreYouSure'),
  });

  const handleSubscriptionChange = (date: DateTime | null, subKey: string) => {
    if (date) {
      const timestamp = date.toMillis();

      if (companyKey && timestamp && !Number.isNaN(timestamp)) {
        subscriptionStore.setSubscriptionStatus(companyKey, subKey, timestamp);
        setIsEditing(false);
        setSelectedSubscription(null);
        setUnsavedChanges(true);
        subscriptionStore.setMessage(t('subscriptionScreen.unsavedChanges'), 'warning');
      }
    }
  };

  if (subscriptionsAdminStore.loading || companyStore.loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!company || !companyKey) {
    return <Typography>No company found</Typography>;
  }

  const renderResellerSelect = () => (
    <Box sx={{ marginTop: 4 }}>
      <FormControl sx={{ width: '50%' }} fullWidth>
        <InputLabel id="demo-simple-select-label">
          {t('subscriptionScreen.selectReseller')}
        </InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={choosenReseller}
          label={t('subscriptionScreen.selectReseller')}
          onChange={(event) => {
            setChoosenReseller(event.target.value);
            setUnsavedChanges(true);
            subscriptionStore.setMessage(t('subscriptionScreen.unsavedChanges'), 'warning');
          }}
        >
          <MenuItem value={NO_RESELLER} key="NoReseller">
            No reseller
          </MenuItem>
          {Object.keys(resellers).map((resellerKey) => (
            <MenuItem value={resellers[resellerKey].id} key={resellerKey}>
              {resellers[resellerKey].name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        Prenumerationer
      </Typography>
      {subscriptionStore.message.text && (
        <Alert severity={subscriptionStore.message.severity} sx={{ marginBottom: 2, marginTop: 2 }}>
          {subscriptionStore.message.text}
        </Alert>
      )}
      {auth.isAdmin && renderResellerSelect()}
      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <Card sx={{ p: 2 }}>
          <Table size="small">
            <TableBody>
              {ALL_SUBSCRIPTIONS.map((subKey) => {
                const sub = company.subscriptions?.find((a: { id: string }) => a.id === subKey);
                const endDate = timestampToDate(sub?.data?.endDate) ?? null;
                const dateString = endDate
                  ? `${sub?.data?.status} - ${DateTime.fromJSDate(endDate).toFormat('yyyy LLL dd')}`
                  : 'Not created';

                return (
                  <TableRow key={subKey} sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
                    <TableCell>{subKey}</TableCell>
                    <TableCell>{dateString}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        onClick={() => {
                          setSelectedSubscription({
                            key: subKey,
                            endDate,
                          });
                          setIsEditing(true);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </Box>
      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <Button
          variant="contained"
          className="btn btn-primary btn-lg"
          disabled={loading || !unsavedChanges}
          onClick={() => {
            // if admin, use admin save, else reseller save
            setLoading(true);
            if (auth.isAdmin) {
              subscriptionStore
                .saveSubscription(companyKey)
                .catch((err) => {
                  subscriptionStore.setMessage(err.message, 'error');
                })
                .finally(() => {
                  setLoading(false);
                  setUnsavedChanges(false);
                  subscriptionStore.setMessage(t('subscriptionScreen.savedChanges'), 'success');
                });
              // Check if the reseller has changed
              if (choosenReseller !== company.resellerId) {
                subscriptionStore
                  .updateReseller(
                    companyKey,
                    choosenReseller === NO_RESELLER ? '' : choosenReseller,
                  )
                  .catch((err) => {
                    subscriptionStore.setMessage(err.message, 'error');
                  })
                  .finally(() => {
                    setLoading(false);
                    setUnsavedChanges(false);
                    subscriptionStore.setMessage(t('subscriptionScreen.savedChanges'), 'success');
                  });
              }
            } else if (auth.reseller?.id) {
              subscriptionStore
                .saveResellerSubscription(companyKey, auth.reseller?.id)
                .catch((err) => {
                  subscriptionStore.setMessage(err.message, 'error');
                })
                .finally(() => {
                  setLoading(false);
                });
            } else {
              subscriptionStore.setMessage('No reseller id found', 'error');
              setLoading(false);
            }
          }}
        >
          Save
          {loading && <CircularProgress />}
        </Button>
      </Box>
      <SubscriptionEditDrawer
        open={isEditing}
        onClose={() => {
          setIsEditing(false);
          setSelectedSubscription(null);
        }}
        handleSubscriptionChange={handleSubscriptionChange}
        subscription={selectedSubscription}
      />
    </Box>
  );
});

export default Subscriptions;
